/**
 * Hook for applying bolt configuration
 *
 * This hook provides a clean interface for UI components to apply bolt configuration.
 * Following SoC, this hook:
 * - Manages React state (processing, result)
 * - Coordinates between store and service
 * - Does NOT contain presentation logic (notifications are built by UI)
 * - Does NOT control navigation timing (UI decides when to navigate)
 */

import { useState, useCallback, useMemo } from 'react';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import {
  useUIStore,
  type ConnectorParameterUndoPlan,
} from '../../stores/useUIStore';
import { useBoltConfiguration } from './useConfigurationForms';
import {
  useActiveDocumentInfoContext,
  useActiveProjectKey,
} from '../../hooks/ActiveDocumentProvider';
import {
  applyBoltConfiguration,
  type BoltConfigurationResult,
} from '../../services/boltConfigurationService';
import {
  type BoltModelingType,
  buildBoltFamilySnapshotRequest,
  getBoltBaseFamilyValue,
  getTargetObjectType,
} from '../../services/boltParameterSetup';
import { createBoltUndoPlan } from '../../services/boltParameterSetup/undoPlan';
import { toBoltConfigurationInput } from '../utils/boltConfigurationInput';
import {
  fetchFamilySnapshotResult,
  invalidateFamilySnapshotCache,
} from '../revit/familySnapshotCache';
import {
  undoAddedParametersToSpecificFamilies,
  undoAddedProjectParameters,
} from '../../api/revit/revitBridge';
import { restoreRemovedBoltFamilyParameters } from '../../services/boltParameterSetup/familyParameterHostActions';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import {
  mapRevitOperationSnapshotToProgressModel,
  type OperationProgressModel,
} from '../../ui/progress';

// ============================================================================
// Types
// ============================================================================

export interface UseBoltConfigurationApplyResult {
  /** Whether the configuration is being applied */
  isProcessing: boolean;
  /** Real backend progress for the active bolt setup operation */
  progressModel: OperationProgressModel | null;
  /** Result from the last apply operation (null if not yet applied) */
  result: BoltConfigurationResult | null;
  /** Apply the bolt configuration */
  apply: () => Promise<BoltConfigurationResult>;
  /** Sync bolt attributes to mapping configuration */
  syncToMapping: () => void;
  /** Reset the result state */
  resetResult: () => void;
  /** Undo the last bolt parameter setup for the active project */
  undoLastBoltParameterAddition: () => Promise<boolean>;
  /** Last undoable bolt parameter setup plan */
  lastBoltUndoPlan: ConnectorParameterUndoPlan | null;
  /** Get the target object type (BOLT or GASKET) based on current choice */
  targetObjectType: BoltModelingType;
  /** Get the bolt base family value based on current choice */
  boltBaseFamilyValue: 'BoltBased' | 'GasketBased';
}

function buildExpectedDocumentIdentity(activeDocumentInfo: {
  documentName?: string | null;
  documentPath?: string | null;
}) {
  const expectedDocumentName = activeDocumentInfo.documentName?.trim();
  const expectedDocumentPath = activeDocumentInfo.documentPath?.trim();

  if (!expectedDocumentName && !expectedDocumentPath) {
    return undefined;
  }

  return {
    ...(expectedDocumentName ? { expectedDocumentName } : {}),
    ...(expectedDocumentPath ? { expectedDocumentPath } : {}),
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for applying bolt configuration
 */
export function useBoltConfigurationApply(): UseBoltConfigurationApplyResult {
  // Get bolt configuration from store
  const { config: boltConfig } = useBoltConfiguration();
  const activeProjectKey = useActiveProjectKey();
  const activeDocumentInfo = useActiveDocumentInfoContext();

  // Get sync action from configuration store
  const syncBoltAttributesToMapping = useConfigurationStore(
    (state) => state.syncBoltAttributesToMapping
  );
  const lastBoltUndoPlan = useUIStore((state) => state.boltParameterUndoPlan);
  const setBoltParameterUndoPlan = useUIStore((state) => state.setBoltParameterUndoPlan);

  // Local state - only processing and result, no presentation state
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<BoltConfigurationResult | null>(null);
  const [progressModel, setProgressModel] = useState<OperationProgressModel | null>(null);

  // Derived values using service functions
  const targetObjectType = useMemo(
    () => getTargetObjectType(boltConfig.boltModelingChoice.value),
    [boltConfig.boltModelingChoice.value]
  );

  const boltBaseFamilyValue = useMemo(
    () => getBoltBaseFamilyValue(boltConfig.boltModelingChoice.value),
    [boltConfig.boltModelingChoice.value]
  );

  const startProgress = useCallback((model: Pick<OperationProgressModel, 'title' | 'message'> & Partial<OperationProgressModel>) => {
    setProgressModel({
      status: 'running',
      percent: null,
      locked: true,
      ...model,
    });
  }, []);

  const updateProgressFromHost = useCallback((progress: RevitOperationProgressSnapshot) => {
    setProgressModel((current) => mapRevitOperationSnapshotToProgressModel(progress, {
      title: current?.title ?? 'Preparing bolt parameters',
      message: 'Revit is processing bolt setup',
      completeMessage: current?.message || 'Bolt setup completed',
      errorMessage: 'Bolt setup failed',
      metrics: current?.metrics,
      locked: !progress.resultReady && !progress.failed,
    }));
  }, []);

  // Apply configuration - returns result, does not handle navigation
  const apply = useCallback(async (): Promise<BoltConfigurationResult> => {
    const liveConfiguration = useConfigurationStore.getState();
    const frozenBoltConfig = liveConfiguration.boltConfiguration;
    const frozenConnectorTarget = liveConfiguration.connectorBindingConfiguration;
    const frozenTargetObjectType = getTargetObjectType(
      frozenBoltConfig.boltModelingChoice.value
    );
    setIsProcessing(true);
    setResult(null);
    startProgress({
      title: 'Preparing bolt connection parameters',
      message: `Revit is checking ${frozenTargetObjectType} families`,
      detail: `${frozenTargetObjectType} family parameters and project bindings are being reconciled.`,
    });

    try {
      const serviceInput = toBoltConfigurationInput(frozenBoltConfig, {
        parameterName: liveConfiguration.menuConfiguration.objectTypeValue,
        bindingKind: frozenConnectorTarget.parameterBindingKind,
      });

      const snapshotRequest = buildBoltFamilySnapshotRequest(
        serviceInput,
        frozenConnectorTarget.parameterDestination
      );
      const familySnapshot = await fetchFamilySnapshotResult({
        cacheScopeKey: activeProjectKey,
        ...snapshotRequest,
        onProgress: updateProgressFromHost,
      });
      if (!familySnapshot.available) {
        throw new Error(familySnapshot.error || 'Revit family data is unavailable.');
      }
      const families = familySnapshot.families;

      // Apply using service
      const configResult = await applyBoltConfiguration(serviceInput, families, {
        expectedDocumentIdentity: buildExpectedDocumentIdentity(activeDocumentInfo),
        onProgress: updateProgressFromHost,
      });

      if (configResult.action !== 'no_action') {
        invalidateFamilySnapshotCache(activeProjectKey);
      }
      const undoPlan = activeProjectKey
        ? createBoltUndoPlan({
            result: configResult,
            configInput: serviceInput,
            familiesBeforeApply: families,
            projectKey: activeProjectKey,
          })
        : null;
      setBoltParameterUndoPlan(undoPlan);
      setResult(configResult);
      return undoPlan
        ? { ...configResult, undoAvailable: true }
        : configResult;
    } catch (error) {
      console.error('[useBoltConfigurationApply] Apply error:', error);
      const errorResult: BoltConfigurationResult = {
        success: false,
        action: 'error',
        message: 'Failed to apply bolt configuration',
        targetObjectType: frozenTargetObjectType,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
      setResult(errorResult);
      return errorResult;
    } finally {
      setIsProcessing(false);
    }
  }, [
    activeDocumentInfo,
    activeProjectKey,
    setBoltParameterUndoPlan,
    startProgress,
    updateProgressFromHost,
  ]);

  // Sync to mapping - separate action, UI decides when to call
  const syncToMapping = useCallback(() => {
    syncBoltAttributesToMapping();
  }, [syncBoltAttributesToMapping]);

  // Reset result
  const resetResult = useCallback(() => {
    setResult(null);
  }, []);

  const undoLastBoltParameterAddition = useCallback(async (): Promise<boolean> => {
    if (!lastBoltUndoPlan || lastBoltUndoPlan.status !== 'available') {
      return false;
    }

    if (!activeProjectKey || lastBoltUndoPlan.projectKey !== activeProjectKey) {
      setBoltParameterUndoPlan(null);
      return false;
    }

    setIsProcessing(true);
    startProgress({
      title: 'Reverting bolt parameters',
      message: 'Revit is undoing bolt parameter setup',
      detail: 'Project bindings and family parameters are restored where an undo plan is available.',
    });
    try {
      const expectedDocumentIdentity = buildExpectedDocumentIdentity(activeDocumentInfo);
      if (lastBoltUndoPlan.projectParameterNames.length > 0) {
        const projectResult = await undoAddedProjectParameters({
          parameterNames: Array.from(new Set(lastBoltUndoPlan.projectParameterNames)),
          ...expectedDocumentIdentity,
          onProgress: updateProgressFromHost,
        });

        if (!projectResult.success) {
          setBoltParameterUndoPlan({
            ...lastBoltUndoPlan,
            status: 'undo-failed',
            error: projectResult.error || 'Failed to undo added bolt project parameters.',
          });
          return false;
        }
      }

      if (lastBoltUndoPlan.entries.length > 0) {
        const familyResult = await undoAddedParametersToSpecificFamilies({
          entries: lastBoltUndoPlan.entries,
          ...expectedDocumentIdentity,
          onProgress: updateProgressFromHost,
        });
        invalidateFamilySnapshotCache(activeProjectKey);

        if (!familyResult.success) {
          setBoltParameterUndoPlan({
            ...lastBoltUndoPlan,
            status: 'undo-failed',
            error: familyResult.error || 'Failed to undo added bolt family parameters.',
          });
          return false;
        }
      }

      if ((lastBoltUndoPlan.familyParameterRestoreEntries ?? []).length > 0) {
        const restoreResult = await restoreRemovedBoltFamilyParameters({
          entries: lastBoltUndoPlan.familyParameterRestoreEntries ?? [],
          expectedDocumentIdentity,
          onProgress: updateProgressFromHost,
        });
        invalidateFamilySnapshotCache(activeProjectKey);

        if (!restoreResult.success) {
          setBoltParameterUndoPlan({
            ...lastBoltUndoPlan,
            status: 'undo-failed',
            error: restoreResult.error || 'Failed to restore removed bolt family parameters.',
          });
          return false;
        }
      }

      invalidateFamilySnapshotCache(activeProjectKey);
      setBoltParameterUndoPlan({
        ...lastBoltUndoPlan,
        status: 'undone',
        error: undefined,
      });
      return true;
    } finally {
      setIsProcessing(false);
    }
  }, [
    activeDocumentInfo,
    activeProjectKey,
    lastBoltUndoPlan,
    setBoltParameterUndoPlan,
    startProgress,
    updateProgressFromHost,
  ]);

  return {
    isProcessing,
    progressModel,
    result,
    apply,
    syncToMapping,
    resetResult,
    undoLastBoltParameterAddition,
    lastBoltUndoPlan,
    targetObjectType,
    boltBaseFamilyValue,
  };
}

export default useBoltConfigurationApply;
